import { useEffect, useState } from "react";
import { BellRinging, FloppyDisk } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { FOLLOWUP_TYPES } from "@/lib/constants";
import { PageHeader, SectionTitle } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const DEFAULTS = {
  channels: { email: true, whatsapp: false, sms: false },
  lead_time_days: 1,
  digest_time: "08:00",
  types: Object.fromEntries(Object.keys(FOLLOWUP_TYPES).map((k) => [k, true])),
};

export default function ReminderSettings() {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/settings/reminders").then((r) => setForm({ ...DEFAULTS, ...(r.data || {}), channels: { ...DEFAULTS.channels, ...(r.data?.channels || {}) }, types: { ...DEFAULTS.types, ...(r.data?.types || {}) } })).catch(() => setForm(DEFAULTS));
  }, []);

  const save = async () => {
    setBusy(true);
    try { await api.put("/settings/reminders", form); toast.success("Reminder settings saved"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!form) return <div className="text-muted-foreground font-mono text-sm animate-pulse">Loading reminder settings…</div>;

  return (
    <div className="space-y-6 max-w-2xl" data-testid="reminder-settings-page">
      <PageHeader overline="Follow-ups" title="Reminder Settings" subtitle="Control how and when reminders reach you and your team" />

      <Card className="p-6 border-border space-y-5">
        <SectionTitle icon={BellRinging}>Channels</SectionTitle>
        {[
          { k: "email", label: "Email", note: "Always available — daily digest and due-today reminders" },
          { k: "whatsapp", label: "WhatsApp", note: "Requires Twilio credentials under My Account → Integrations" },
          { k: "sms", label: "SMS", note: "Requires an SMS provider — not configured yet" },
        ].map((c) => (
          <div key={c.k} className="flex items-start justify-between gap-4">
            <div>
              <Label className="text-sm">{c.label}</Label>
              <p className="text-xs text-muted-foreground mt-0.5">{c.note}</p>
            </div>
            <Switch
              checked={!!form.channels[c.k]}
              onCheckedChange={(v) => setForm({ ...form, channels: { ...form.channels, [c.k]: v } })}
              data-testid={`channel-${c.k}`}
            />
          </div>
        ))}
      </Card>

      <Card className="p-6 border-border space-y-4">
        <SectionTitle>Timing</SectionTitle>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label>Lead time before due date</Label>
            <Select value={String(form.lead_time_days)} onValueChange={(v) => setForm({ ...form, lead_time_days: parseInt(v, 10) })}>
              <SelectTrigger data-testid="lead-time"><SelectValue /></SelectTrigger>
              <SelectContent>{[0, 1, 2, 3, 7].map((d) => <SelectItem key={d} value={String(d)}>{d === 0 ? "On the due date" : `${d} day${d > 1 ? "s" : ""} before`}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Daily digest time (IST)</Label>
            <Select value={form.digest_time} onValueChange={(v) => setForm({ ...form, digest_time: v })}>
              <SelectTrigger data-testid="digest-time"><SelectValue /></SelectTrigger>
              <SelectContent>{["07:00", "07:30", "08:00", "09:00", "10:00", "18:00"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <p className="text-xs text-muted-foreground">The morning digest currently runs at 08:00 IST and the staff reminder at 07:30 IST. Changing the time here records your preference; the schedule itself is updated by us on request.</p>
      </Card>

      <Card className="p-6 border-border space-y-3">
        <SectionTitle>Which reminder types to send</SectionTitle>
        <div className="grid sm:grid-cols-2 gap-3">
          {Object.entries(FOLLOWUP_TYPES).map(([k, label]) => (
            <div key={k} className="flex items-center justify-between gap-3 p-2 rounded-md bg-muted border border-border">
              <span className="text-sm text-primary">{label}</span>
              <Switch checked={!!form.types[k]} onCheckedChange={(v) => setForm({ ...form, types: { ...form.types, [k]: v } })} data-testid={`rtype-${k}`} />
            </div>
          ))}
        </div>
      </Card>

      <Button onClick={save} disabled={busy} className="rounded-full bg-primary hover:bg-[#113A64]" data-testid="save-reminders-btn">
        <FloppyDisk size={16} className="mr-1" /> {busy ? "Saving…" : "Save Reminder Settings"}
      </Button>
    </div>
  );
}
